import { useCallback, useEffect, useRef, useState } from 'react'
import type { TurnstileWidgetHandle } from '../components/common/TurnstileWidget'

interface VerificationToken {
  value: string
  contextKey: string
}

export function useEmailCodeVerification(contextKey = '') {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY
  const [token, setTokenState] = useState<VerificationToken | null>(null)
  const [sentContext, setSentContext] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const widgetRef = useRef<TurnstileWidgetHandle | null>(null)
  const tokenRef = useRef<VerificationToken | null>(null)
  const sendingRef = useRef(false)
  const generationRef = useRef(0)
  const contextRef = useRef(contextKey)
  contextRef.current = contextKey

  const setToken = useCallback((value: string | null) => {
    const nextToken = value ? { value, contextKey } : null
    tokenRef.current = nextToken
    setTokenState(nextToken)
  }, [contextKey])

  const reset = useCallback(() => {
    generationRef.current += 1
    tokenRef.current = null
    setTokenState(null)
    setSentContext(null)
    setError(null)
    widgetRef.current?.reset()
  }, [])

  useEffect(() => {
    reset()
  }, [contextKey, reset])

  useEffect(() => () => { generationRef.current += 1 }, [])

  const completed = sentContext === contextKey
  useEffect(() => {
    if (!completed) return
    const timer = window.setTimeout(reset, 10 * 60 * 1000)
    return () => window.clearTimeout(timer)
  }, [completed, reset])

  const send = async <T,>(request: (turnstileToken?: string) => Promise<T>): Promise<T> => {
    if (sendingRef.current) throw new Error('验证码正在发送，请稍候')
    const currentToken = tokenRef.current
    if (contextRef.current !== contextKey || (siteKey && (!currentToken || currentToken.contextKey !== contextKey))) {
      throw new Error(error ?? '请先完成人机验证')
    }
    const generation = generationRef.current
    sendingRef.current = true
    setSending(true)
    tokenRef.current = null
    setTokenState(null)
    setError(null)
    try {
      const result = await request(currentToken?.value)
      if (generationRef.current !== generation || contextRef.current !== contextKey) {
        throw new Error('邮箱或验证状态已变化，请重新获取验证码')
      }
      setSentContext(contextKey)
      return result
    } catch (failure) {
      if (generationRef.current === generation && contextRef.current === contextKey) reset()
      throw failure
    } finally {
      sendingRef.current = false
      setSending(false)
    }
  }

  const hasToken = Boolean(token && token.contextKey === contextKey)
  return {
    siteKey, error, widgetRef, setToken, setError, reset, send, sending, completed,
    canSend: !sending && (!siteKey || hasToken),
    ready: !sending && (!siteKey || hasToken || completed),
  }
}
